import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Button,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { AdditionalFieldConfig } from "@/features/accountSettings/types/additionalFields";
import type { AdditionalFieldsFormData } from "@/features/map/types";
import { CAdditionalFieldInput } from "./CAdditionalFieldInput";

interface AdditionalFieldsModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: AdditionalFieldsFormData) => void | Promise<void>;
  availableFields: AdditionalFieldConfig | undefined;
  initialValues?: Record<string, string> | null;
  title?: string;
}

/**
 * 追加項目編集モーダルコンポーネント
 * アカウント設定で定義された項目（deleted_at: null）のみ編集可能
 */
export const AdditionalFieldsModal: React.FC<AdditionalFieldsModalProps> = ({
  open,
  onClose,
  onSave,
  availableFields,
  initialValues,
  title = "カスタム項目を編集",
}) => {
  // Zodスキーマを動的に生成
  const schema = React.useMemo(() => {
    if (!availableFields?.fields) return z.object({});

    const schemaFields: Record<string, any> = {};
    availableFields.fields.forEach((field) => {
      // フィールドタイプに応じたバリデーションを追加
      if (field.fieldType === "text" || field.fieldType === "textarea") {
        // テキストフィールド・テキストエリア: 500文字まで
        schemaFields[field.id] = z
          .string()
          .max(500, `${field.key}は500文字以内で入力してください`);
      } else if (field.fieldType === "number") {
        // 数値フィールド: 15桁まで（JavaScriptの安全な整数範囲）
        schemaFields[field.id] = z.string().refine(
          (val) => {
            if (!val || val === "") return true;
            const numStr = val.replace(/^-/, "");
            return numStr.length <= 15;
          },
          { message: `${field.key}は15桁以内で入力してください` }
        );
      } else {
        // その他のフィールドタイプ（date, datetime, booleanなど）
        schemaFields[field.id] = z.string();
      }
    });

    return z.object(schemaFields);
  }, [availableFields]);

  type FormData = z.infer<typeof schema>;

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<FormData>({
    mode: "onChange",
    resolver: zodResolver(schema),
    defaultValues: {},
  });

  const [isSaving, setIsSaving] = useState(false);

  // 初期値の設定
  useEffect(() => {
    if (!availableFields?.fields) {
      reset({});
      return;
    }

    // initialValuesはすでにAdditionalFieldsFormData形式({uuid: "value"})なので、
    // そのまま使用する
    const defaultValues: Record<string, string> = {};
    availableFields.fields.forEach((field) => {
      // initialValuesから値を取得、存在しない場合は空文字列
      const value = initialValues?.[field.id];
      // NOTE: React Hook Formでは全ての値を文字列として扱う必要があるため、
      // number型のフィールドでもString()で明示的に文字列に変換する
      defaultValues[field.id] =
        value !== null && value !== undefined ? String(value) : "";
    });

    reset(defaultValues);
  }, [availableFields, initialValues, reset]);

  const onSubmit = async (data: FormData): Promise<void> => {
    if (isSaving) return; // 二重送信防止

    try {
      setIsSaving(true);
      await onSave(data);
    } catch (error) {
      // エラーが発生した場合はモーダルを閉じない
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (): void => {
    onClose();
  };

  if (!availableFields?.fields || availableFields.fields.length === 0) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // バックドロップクリックでの閉じる操作を無効化
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown={true}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {title}
        <Button
          variant="outlined"
          onClick={handleCancel}
          sx={{
            position: "absolute",
            right: 16,
            top: 16,
            minWidth: "auto",
            padding: "4px 12px",
            fontSize: "0.875rem",
          }}
        >
          閉じる
        </Button>
      </DialogTitle>
      <form
        onSubmit={(e) => {
          void handleSubmit(onSubmit)(e);
        }}
      >
        <DialogContent
          sx={{ maxHeight: "calc(90vh - 200px)", overflowY: "auto" }}
        >
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              {availableFields.fields.map((field) => (
                <Grid item xs={12} key={field.id}>
                  <CAdditionalFieldInput
                    control={control}
                    name={field.id as keyof FormData}
                    label={field.key}
                    fieldType={field.fieldType}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCancel} disabled={isSaving}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={isSaving || !isValid}
          >
            保存
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
